import { useStorage } from '@vueuse/core'
import { getCurrentInstance, onUnmounted, ref, shallowRef, type VNode } from 'vue'

import { reloadOnHmr } from '../hmr.js'

export interface AppDrawerSubscription {
  /**
   * Unique per registration. Used as the rail's `v-for` key so a target's
   * lifetime is exactly its subscription's — labels are NOT unique and must
   * never key the list.
   */
  id: string
  label: string
  /** The bare DOM id the subscriber teleports into (no leading `#`). */
  target: string
}

let nextSubscriptionId = 0

const normalizeTarget = (target: string): string => {
  return target.startsWith('#') ? target.slice(1) : target
}

const showContextDrawer = ref(false)
const toggleShowContextDrawer = (): void => {
  showContextDrawer.value = !showContextDrawer.value
}

const subscribers = shallowRef<AppDrawerSubscription[]>([])
const subscribe = (label: string, target: string): (() => void) => {
  const normalizedTarget = normalizeTarget(target)
  // A DOM id is unique. A re-mounting owner (e.g. a nav rendered by every view
  // of a module) replaces its predecessor, so the rail can never render two
  // targets with the same id — `querySelector` would bind every teleport to the
  // first and the rest would orphan.
  subscribers.value = subscribers.value.filter((subscription) => {
    return subscription.target !== normalizedTarget
  })
  const subscription: AppDrawerSubscription = {
    id: `app-drawer-subscription-${nextSubscriptionId++}`,
    label,
    target: normalizedTarget,
  }
  addSubscriber(subscription)
  const unsubscribe = (): void => {
    removeSubscriber(subscription.id)
  }
  // `subscribe` is normally called from a component setup; guard so a caller
  // outside one (module scope) does not trip Vue's "no active instance" warning.
  if (getCurrentInstance()) {
    onUnmounted(() => {
      unsubscribe()
    })
  }
  return unsubscribe
}
const addSubscriber = (subscription: AppDrawerSubscription): AppDrawerSubscription => {
  subscribers.value = [...subscribers.value, subscription]
  return subscription
}
const removeSubscriber = (subscriptionId: string): void => {
  subscribers.value = subscribers.value.filter((subscription) => {
    return subscription.id !== subscriptionId
  })
}

/**
 * A module's contextual rail action (e.g. "Create Product", "Insights").
 *
 * Owned by the module's nav — the component every view of the module mounts —
 * so its position in the rail is stable across the module's views. The active
 * view registers the module's actions as a **list**; when no view registers any
 * the slot renders nothing.
 */
export interface AppDrawerModuleAction {
  label: string
  tooltip?: string
  /** The item's icon/badge content — a render function, so the module keeps full control. */
  content: () => VNode
  onClick: () => void
  /**
   * The action for the view you are ON (e.g. an insight's own route, reached from the module):
   * rendered muted and inert so the affordance and its position survive leaving the host screen.
   */
  current?: boolean
}

/**
 * The module's rail actions, one list per module key. A list (not a single
 * action) because a module can carry more than one contextual action — e.g.
 * Product's *Create* alongside an *Insights* toggle. Before this was widened a
 * second registration replaced the first and the earlier action vanished.
 *
 * The set is owned as a whole: the view that registered it supplies the full
 * list, and a successor view's set replaces the predecessor's. That mirrors the
 * previous singular contract (`RichContentManager` → `RichContentBlocksManager`),
 * so the active view stays the single authority for what the module shows.
 */
const moduleActions = shallowRef<Record<string, AppDrawerModuleAction[] | undefined>>({})
const moduleActionOwners: Record<string, symbol | undefined> = {}
const setModuleActions = (moduleKey: string, actions: AppDrawerModuleAction[]): void => {
  const token = Symbol(moduleKey)
  moduleActionOwners[moduleKey] = token
  // Copy: the caller keeps no handle on our registry entry.
  moduleActions.value = { ...moduleActions.value, [moduleKey]: [...actions] }
  if (getCurrentInstance()) {
    onUnmounted(() => {
      // A successor view may have registered first; only the owner clears.
      if (moduleActionOwners[moduleKey] !== token) {
        return
      }
      moduleActionOwners[moduleKey] = undefined
      const next = { ...moduleActions.value }
      delete next[moduleKey]
      moduleActions.value = next
    })
  }
}
/**
 * Register a single module action.
 *
 * Retained as a one-element wrapper around {@link setModuleActions} so existing
 * registrants (`ProductManager`, `RichContentManager`, `RichContentBlocksManager`)
 * are untouched. Prefer `setModuleActions` for a module with more than one action.
 */
const setModuleAction = (moduleKey: string, action: AppDrawerModuleAction): void => {
  setModuleActions(moduleKey, [action])
}

const railPinned = useStorage('bf.appDrawer.railPinned', false)
const toggleRailPinned = (): void => {
  railPinned.value = !railPinned.value
}

const mobilePanelOpen = ref(false)
const toggleMobilePanel = (): void => {
  mobilePanelOpen.value = !mobilePanelOpen.value
}
const closeMobilePanel = (): void => {
  mobilePanelOpen.value = false
}

const viewTitle = ref<string | null>(null)
let viewTitleOwner: symbol | null = null
const setViewTitle = (title: string | null): void => {
  viewTitle.value = title
  viewTitleOwner = null
}
export const useViewTitle = (title: string): void => {
  const token = Symbol('appDrawerViewTitle')
  viewTitleOwner = token
  viewTitle.value = title
  onUnmounted(() => {
    if (viewTitleOwner === token) {
      viewTitleOwner = null
      viewTitle.value = null
    }
  })
}

export const useAppDrawer = (): {
  showContextDrawer: typeof showContextDrawer
  toggleShowContextDrawer: typeof toggleShowContextDrawer
  subscribers: typeof subscribers
  subscribe: typeof subscribe
  moduleActions: typeof moduleActions
  setModuleActions: typeof setModuleActions
  setModuleAction: typeof setModuleAction
  railPinned: typeof railPinned
  toggleRailPinned: typeof toggleRailPinned
  mobilePanelOpen: typeof mobilePanelOpen
  toggleMobilePanel: typeof toggleMobilePanel
  closeMobilePanel: typeof closeMobilePanel
  viewTitle: typeof viewTitle
  setViewTitle: typeof setViewTitle
} => {
  return {
    showContextDrawer,
    toggleShowContextDrawer,
    subscribers,
    subscribe,
    moduleActions,
    setModuleActions,
    setModuleAction,
    railPinned,
    toggleRailPinned,
    mobilePanelOpen,
    toggleMobilePanel,
    closeMobilePanel,
    viewTitle,
    setViewTitle,
  }
}

// Module-scope state: reload (not hot-swap) on edit so instances never diverge.
reloadOnHmr()
